import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatError } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TimezoneInputComponent } from '../../../../../shared/components/timezone-input/timezone-input.component';
import moment from 'moment-timezone';
import { IScheduleEdit } from '../../../models/schedule-calendar.models';
import { toAvailableSchedulePeriods } from '../../../utils/schedule-timezone.utils';
import {
  CalendarAvailableBlock,
  formatMinutes,
} from '../../../utils/schedule-calendar.utils';
import { ScheduleTimesFieldsComponent } from '../../forms/schedule-times-fields/schedule-times-fields.component';
import {
  createTimesControls,
  toAvailableTimes,
} from '../../forms/schedule-times-fields/schedule-times-fields.form';
import { timesNotSkippedValidator } from '../../../validators/schedule-calendar.validators';

export interface IScheduleEditDialogData {
  block: CalendarAvailableBlock;
  /** The timezone the block is shown in, which the times start in */
  timezoneName: string;
}

@Component({
  selector: 'app-schedule-edit-dialog',
  templateUrl: './schedule-edit-dialog.component.html',
  styleUrls: ['./schedule-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatDialogModule,
    MatButtonModule,
    MatError,
    MatIconModule,
    ScheduleTimesFieldsComponent,
    TimezoneInputComponent,
  ],
})
export class ScheduleEditDialogComponent {
  private readonly data = inject<IScheduleEditDialogData>(MAT_DIALOG_DATA);
  readonly block = this.data.block;
  private readonly dialogRef =
    inject<MatDialogRef<ScheduleEditDialogComponent, IScheduleEdit>>(
      MatDialogRef
    );

  private readonly time = {
    start: formatMinutes(this.block.start),
    end: formatMinutes(this.block.end),
  };

  /** The block's date and the times are in this timezone */
  readonly timezoneName = signal(this.data.timezoneName);

  private readonly timezoneInput = viewChild.required(TimezoneInputComponent);

  readonly form = new FormGroup(
    createTimesControls({
      time: this.time,
      allPreferred: this.isAllPreferred(),
      preferredTimes: this.isAllPreferred()
        ? []
        : this.block.preferred.map(({ start, end }) => ({
            start: formatMinutes(start),
            end: formatMinutes(end),
          })),
    }),
    timesNotSkippedValidator(
      () => [this.block.date],
      () => this.timezoneName()
    )
  );

  save() {
    this.form.updateValueAndValidity();
    this.timezoneInput().touch();

    if (this.form.invalid || !this.timezoneInput().isValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const { date, range } = this.block;
    const timezoneName = this.timezoneName();
    // Keeps times the clocks going back repeat on the block's side of the change
    const preferredUtcOffset =
      timezoneName === this.data.timezoneName
        ? moment(range.start).tz(timezoneName).utcOffset()
        : undefined;

    this.dialogRef.close({
      added: toAvailableSchedulePeriods(
        date,
        toAvailableTimes(this.form.getRawValue()),
        timezoneName,
        preferredUtcOffset
      ),
      removed: [range],
    });
  }

  delete() {
    this.dialogRef.close({
      added: [],
      removed: [this.block.range],
    });
  }

  getSkippedTimesError() {
    return this.form.controls.time.touched && this.form.hasError('timesSkipped')
      ? 'These times are skipped when the clocks go forward'
      : null;
  }

  private isAllPreferred() {
    if (this.block.preferred.length > 1) {
      return false;
    }

    const {
      start,
      end,
      preferred: [preferred],
    } = this.block;

    return preferred?.start === start && preferred?.end === end;
  }
}
